import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import shared from './GarageContent.module.scss';
import classes from './InspectionCard.module.scss';
import VehicleStrip from './VehicleStrip';
import StatusTimeline from './StatusTimeline';
import MediaGallery from './MediaGallery';
import DealerWaitNote from './DealerWaitNote';
import useFocusedCard from './useFocusedCard';
import { carFromSnapshot, formatDay } from '../../services/dealerGarage';
import { buildVehicleTitle } from '../../helper/vehicleTitle';

const REASON_MAX = 2000;
// Mirrors the API's limits on the "what to check" list.
const CHECK_MAX_LENGTH = 500;
const CHECKS_MAX = 30;
// While the inspection is still ahead, the customer can add to the list.
const CHECKS_OPEN_STATUSES = ['Requested', 'Accepted', 'Scheduled'];
const PHONE = '703-249-9922';

// The chip on the card: the inspection's status, or once the report is in,
// where the customer's decision stands.
const chipFor = (inspection) => {
    const decision = inspection.decision?.value || null;
    switch (inspection.status) {
        case 'Requested': return { label: 'Requested', tone: shared.toneMuted };
        // Staff accept a request; the customer sees it as confirmed.
        case 'Accepted': return { label: 'Confirmed', tone: shared.toneInfo };
        case 'Scheduled': return { label: 'Scheduled', tone: shared.toneInfo };
        case 'Cancelled': return { label: 'Cancelled', tone: shared.toneDanger };
        case 'Completed':
            if (decision === 'buy') return { label: 'Buying', tone: shared.toneSuccess };
            if (decision === 'decline') return { label: 'Passed', tone: shared.toneMuted };
            return { label: 'Report ready', tone: shared.toneBrand };
        default: return { label: inspection.status, tone: shared.toneMuted };
    }
};

/**
 * "What you'd like us to check" while the inspection is still ahead: the
 * customer's own items can be taken back, the team's items are marked as
 * theirs, and a compact box adds more.
 *
 * `onAdd(text)` / `onRemove(checkId)` resolve to { ok, message }.
 */
const ChecklistEditor = ({ inspectionId, checks, onAdd, onRemove }) => {
    const [draft, setDraft] = useState('');
    const [adding, setAdding] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [error, setError] = useState('');
    const inputId = `inspection-check-${inspectionId}`;
    const full = checks.length >= CHECKS_MAX;

    const add = async (e) => {
        e.preventDefault();
        const text = draft.trim();
        if (!text || adding) return;
        setAdding(true);
        setError('');
        const result = await onAdd(text);
        setAdding(false);
        // Keep the text when it did not go through, so nothing is lost.
        if (result.ok) setDraft('');
        else setError(result.message);
    };

    const remove = async (checkId) => {
        if (removingId) return;
        setRemovingId(checkId);
        setError('');
        const result = await onRemove(checkId);
        setRemovingId(null);
        if (!result.ok) setError(result.message);
    };

    return (
        <section>
            <h3 className={shared.subhead}>
                <i className="fas fa-tasks" aria-hidden="true"></i>
                What you'd like us to check
            </h3>
            {checks.length === 0 ? (
                <p className={classes.checksEmpty}>Nothing on the list yet. Add anything you'd like the inspector to look at.</p>
            ) : (
                <ul className={classes.checks}>
                    {checks.map((check) => (
                        <li key={check._id}>
                            <span className={classes.checkText}>{check.text}</span>
                            {check.by === 'staff' && <span className={classes.checkBy}>Added by our team</span>}
                            {check.by === 'customer' && (
                                <button
                                    type="button"
                                    className={classes.checkRemove}
                                    onClick={() => remove(check._id)}
                                    disabled={!!removingId}
                                    aria-label={`Remove "${check.text.length > 60 ? `${check.text.slice(0, 60)}…` : check.text}"`}
                                    title="Remove"
                                >
                                    <i className={`fas ${removingId === check._id ? 'fa-spinner fa-spin' : 'fa-times'}`} aria-hidden="true"></i>
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {full ? (
                <p className={classes.checksEmpty}>That's the most one inspection can take - message us about anything else.</p>
            ) : (
                <form className={classes.checkAdd} onSubmit={add}>
                    <label htmlFor={inputId} className={shared.srOnly}>Add something to check</label>
                    <input
                        id={inputId}
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={CHECK_MAX_LENGTH}
                        placeholder="Add something to check"
                        disabled={adding}
                    />
                    <button type="submit" className={shared.actionButton} disabled={adding || !draft.trim()}>
                        {adding ? 'Adding…' : 'Add'}
                    </button>
                </form>
            )}
            {error && <p className={shared.inlineError} role="alert">{error}</p>}
        </section>
    );
};

/** Each item on the list with the inspector's answer, shown with the report. */
const ChecklistFindings = ({ checks }) => (
    <>
        <h4 className={classes.reportSubhead}>What we checked</h4>
        <ul className={classes.findings}>
            {checks.map((check) => (
                <li key={check._id}>
                    <span className={classes.checkText}>{check.text}</span>
                    {check.by === 'staff' && <span className={classes.checkBy}>Added by our team</span>}
                    {check.finding
                        ? <p className={classes.finding}>{check.finding}</p>
                        : <p className={`${classes.finding} ${classes.findingEmpty}`}>No note from the inspector</p>}
                </li>
            ))}
        </ul>
    </>
);

/**
 * Buy / pass on a car once its report is in, or ask about it first. "Not for
 * me" asks what they would like instead - that goes to the team, who keep
 * searching. "Ask a question" opens their conversation with the team, or
 * offers a call when the inspection did not start from one.
 *
 * `onDecide(decision, reason)` resolves to { ok, message }.
 */
const DecisionPanel = ({ inquiryId, onDecide }) => {
    const [mode, setMode] = useState(null); // null | 'buy' | 'decline'
    const [reason, setReason] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    const submit = async (decision) => {
        if (sending) return;
        setSending(true);
        setError('');
        const result = await onDecide(decision, decision === 'decline' ? reason.trim() : '');
        // On success the card swaps this panel for the outcome, so only a
        // failure needs handling here.
        if (!result.ok) {
            setSending(false);
            setError(result.message);
        }
    };

    const back = () => {
        setMode(null);
        setError('');
    };

    return (
        <div className={classes.decision}>
            {mode === null && (
                <>
                    <strong className={classes.decisionTitle}>What do you think?</strong>
                    <p>
                        Take your time with the report, and ask us anything you'd like to know. When you're
                        ready, let us know if you'd like to buy it. If it's not the one, tell us what you're
                        after and we'll keep searching for you.
                    </p>
                    <div className={classes.actions}>
                        <button type="button" className={`${shared.actionButton} ${shared.actionPrimary}`} onClick={() => setMode('buy')}>
                            <i className="fas fa-check" aria-hidden="true"></i> Buy this car
                        </button>
                        {inquiryId
                            ? <Link to={`/garage?tab=inquiries&inquiry=${inquiryId}`} className={shared.actionButton}>Ask a question</Link>
                            : <a href={`tel:${PHONE}`} className={shared.actionButton}>Call us: {PHONE}</a>}
                        <button type="button" className={shared.actionButton} onClick={() => setMode('decline')}>
                            Not for me
                        </button>
                    </div>
                </>
            )}

            {mode === 'buy' && (
                <>
                    <p>We'll let the team know you'd like to buy this car, and we'll be in touch to finalize your purchase.</p>
                    <div className={classes.actions}>
                        <button
                            type="button"
                            className={`${shared.actionButton} ${shared.actionPrimary}`}
                            onClick={() => submit('buy')}
                            disabled={sending}
                        >
                            {sending ? 'Sending…' : 'Yes, buy this car'}
                        </button>
                        <button type="button" className={shared.actionButton} onClick={back} disabled={sending}>
                            Back
                        </button>
                    </div>
                </>
            )}

            {mode === 'decline' && (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit('decline');
                    }}
                >
                    <label className={classes.reasonLabel}>
                        What should we look for instead? <span>(optional)</span>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            maxLength={REASON_MAX}
                            rows={3}
                            placeholder="Model, year, mileage, color, budget…"
                            disabled={sending}
                        />
                    </label>
                    <div className={classes.actions}>
                        <button type="submit" className={`${shared.actionButton} ${shared.actionPrimary}`} disabled={sending}>
                            {sending ? 'Sending…' : 'Pass on this car'}
                        </button>
                        <button type="button" className={shared.actionButton} onClick={back} disabled={sending}>
                            Back
                        </button>
                    </div>
                </form>
            )}

            {error && <p className={shared.inlineError} role="alert">{error}</p>}
        </div>
    );
};

/** What the customer decided, and what the team does about it. */
const DecisionOutcome = ({ decision }) => {
    const buying = decision.value === 'buy';
    return (
        <div className={`${classes.outcome} ${buying ? classes.outcomeBuy : ''}`} role="status">
            <i className={buying ? 'fas fa-check-circle' : 'fas fa-search'} aria-hidden="true"></i>
            <div>
                <strong>{buying ? 'You\'re buying this car' : 'You passed on this car'}</strong>
                <p>{buying ? 'We\'ll be in touch to finalize your purchase.' : 'We\'ll keep searching for you.'}</p>
                {!buying && decision.reason && (
                    <p className={classes.outcomeReason}>You asked us to look for: {decision.reason}</p>
                )}
                {decision.at && <span className={classes.outcomeDate}>{formatDay(decision.at)}</span>}
            </div>
        </div>
    );
};

/** The team's message on the latest step, shown beside the status. */
const TeamNote = ({ note }) => (
    <div className={classes.teamNote}>
        <span>A note from our team</span>
        <p>{note}</p>
    </div>
);

/**
 * One dealer-stock inspection: requested, accepted by the team after talking
 * it through (shown to the customer as "Confirmed" - the deposit is never
 * mentioned here), booked, then the report (grade, summary, the customer's checklist with
 * the inspector's findings, photos and video) and the buy / pass decision.
 *
 * The latest step's note from the team is shown with the status, so the
 * timeline below leaves it out rather than repeat it.
 */
const InspectionCard = ({ inspection, focused, onDecide, onAddCheck, onRemoveCheck }) => {
    const cardRef = useFocusedCard(focused);
    const chip = chipFor(inspection);
    const report = inspection.report || {};
    const decision = inspection.decision || {};
    const images = Array.isArray(inspection.images) ? inspection.images : [];
    const videos = Array.isArray(inspection.videos) ? inspection.videos : [];
    const checks = Array.isArray(inspection.checks) ? inspection.checks : [];
    const history = Array.isArray(inspection.statusHistory) ? inspection.statusHistory : [];
    const latestNote = history.length ? (history[history.length - 1].note || '').trim() : '';
    const completed = inspection.status === 'Completed';
    const checksOpen = CHECKS_OPEN_STATUSES.includes(inspection.status);
    const car = carFromSnapshot(inspection);
    const carTitle = (car.make || car.model) ? buildVehicleTitle(car) : (car.title || 'Car');

    return (
        <article ref={cardRef} className={`${shared.card} ${focused ? shared.cardFocused : ''}`}>
            <div className={shared.cardHead}>
                <div>
                    <span className={shared.typeTag}>Inspection</span>
                    <span className={shared.date}>{formatDay(inspection.createdAt)}</span>
                </div>
                <span className={`${shared.status} ${chip.tone}`}>{chip.label}</span>
            </div>

            <VehicleStrip record={inspection} />

            {inspection.status === 'Requested' && (
                <p className={shared.message}>
                    Thanks for your request! One of our team will reach out shortly to talk through the car
                    with you and plan the inspection. Anything you'd like our inspector to look at? Add it below.
                </p>
            )}

            {inspection.status === 'Accepted' && (
                <p className={shared.message}>
                    Your inspection is confirmed. We're lining up our inspector and will send you the date shortly.
                </p>
            )}

            {inspection.status === 'Scheduled' && (
                <p className={`${shared.message} ${classes.booked}`}>
                    <i className="fas fa-calendar-check" aria-hidden="true"></i>
                    <span>
                        {inspection.scheduledFor
                            ? <>Booked for <strong>{formatDay(inspection.scheduledFor)}</strong>.</>
                            : 'Your inspection is booked.'}
                    </span>
                </p>
            )}

            {inspection.status === 'Cancelled' && (
                <>
                    {latestNote
                        ? <TeamNote note={latestNote} />
                        : <p className={shared.message}>This inspection was cancelled.</p>}
                    <p className={shared.message}>
                        Questions? Call us at <a href={`tel:${PHONE}`} className={classes.inlineLink}>{PHONE}</a> or{' '}
                        {inspection.inquiryId
                            ? <Link to={`/garage?tab=inquiries&inquiry=${inspection.inquiryId}`} className={classes.inlineLink}>send us a message</Link>
                            : 'send us a message'}.
                    </p>
                </>
            )}

            {inspection.status !== 'Cancelled' && latestNote && <TeamNote note={latestNote} />}

            <DealerWaitNote record={inspection} />

            {checksOpen && (
                <ChecklistEditor
                    inspectionId={inspection._id}
                    checks={checks}
                    onAdd={(text) => onAddCheck(inspection._id, text)}
                    onRemove={(checkId) => onRemoveCheck(inspection._id, checkId)}
                />
            )}

            {completed && (
                <section>
                    <h3 className={shared.subhead}>
                        <i className="fas fa-clipboard-check" aria-hidden="true"></i>
                        Inspection report
                        {inspection.completedAt && <span className={classes.reportDate}>{formatDay(inspection.completedAt)}</span>}
                    </h3>
                    {report.grade && (
                        <p className={classes.grade}>
                            <span>Overall</span>
                            <strong>{report.grade}</strong>
                        </p>
                    )}
                    {report.summary && <p className={`${shared.message} ${classes.summary}`}>{report.summary}</p>}
                    {checks.length > 0 && <ChecklistFindings checks={checks} />}
                    <MediaGallery images={images} videos={videos} title={carTitle} />
                    {decision.value
                        ? <DecisionOutcome decision={decision} />
                        : <DecisionPanel inquiryId={inspection.inquiryId} onDecide={(value, reason) => onDecide(inspection._id, value, reason)} />}
                </section>
            )}

            {history.length > 0 && (
                <section>
                    <h3 className={shared.subhead}>
                        <i className="fas fa-history" aria-hidden="true"></i>
                        Timeline
                    </h3>
                    <StatusTimeline
                        history={history.map((e) => (e.status === 'Accepted' ? { ...e, status: 'Confirmed' } : e))}
                        showLatestNote={false}
                    />
                </section>
            )}
        </article>
    );
};

export default InspectionCard;
